import {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { getFleetStatus } from "@/api/asset-api.js";
import {
  isRequestAborted,
  setTenantSwitchInProgress,
} from "@/api/api-utils.js";
import {
  transformApiResponseToAssets,
  calculateFleetStats,
  filterAssetsBySearch,
  enrichAssetsWithDowntimeData,
} from "@/lib/asset-data-utils.js";
import { arrayUtils } from "@/lib/utils.js";
import { formatTimestampForSearch } from "@/lib/datetime-utils.js";
import {
  matchesOptimizationFilter,
  matchesHorsepowerRangeFilter,
} from "@/lib/filter-utils.js";
import { ensureFleetReady } from "@/lib/ensure-fleet-ready.js";
import { useDataLoaderContext } from "./DataLoaderContext.jsx";
import { useSessionTenant } from "./SessionTenantContext.jsx";

/**
 * @typedef {Object} Asset
 * @property {string} id - Asset ID
 * @property {string} assetNumber - Asset Number
 * @property {string} name - Asset name (same as assetNumber)
 * @property {string} status - Status (running, faulted, alerted, offline, shutdown)
 * @property {string} location - Location name
 * @property {string} vendor - Vendor name
 * @property {string} telemetrySource - Telemetry source identifier
 * @property {string|null} foreman - Foreman assigned to asset
 * @property {string|null} customer - Customer assigned to asset
 * @property {number|null} horsepower - Horsepower of asset
 * @property {string|null} compressorModel - CompressorModel of asset if present
 * @property {string|null} engineModel - EngineModel of asset if present
 * @property {string} lastUpdated - Last updated timestamp
 * @property {string|null} lastFault - Last fault description
 * @property {string|null} lastFaultTime - Last fault time
 * @property {number|null} latitude - Latitude
 * @property {number|null} longitude - Longitude
 * @property {number|null} enalysisCompanyId - Enalysis company ID
 * @property {number|null} enalysisCompressorNum - Enalysis compressor number
 * @property {boolean} optimizationEnabled - Whether optimization (Enalysis) is enabled
 */

/**
 * @typedef {Object} FleetStats
 * @property {number} normal - Number of normal compressors
 * @property {number} standby - Number of standby compressors
 * @property {number} nodata - Number of nodata compressors
 * @property {number} offline - Number of offline compressors
 * @property {number} alerted - Number of alerted compressors
 * @property {number} danger - Number of danger compressors
 * @property {number} available - Number of available compressors
 * @property {number} unknown - Number of unknown compressors
 * @property {number} total - Total number of compressors
 */

/**
 * @typedef {Object} AssetContextType
 * @property {Asset[]} assets - Array of all assets
 * @property {FleetStats} fleetStats - Fleet statistics calculated from assets
 * @property {boolean} isLoading - Loading state
 * @property {string|null} error - Error message if any
 * @property {Date|null} lastUpdatedTime - Timestamp of last data update
 * @property {() => Promise<void>} refetch - Function to refetch asset data
 * @property {(downtimeEvents: Array) => void} enrichWithDowntime - Function to enrich assets with downtime data
 */

const AssetContext = createContext(/** @type {AssetContextType|null} */ (null));

/**
 * Hook to use the asset context
 * @returns {AssetContextType} Asset context value
 * @throws {Error} If used outside of AssetProvider
 */
export const useAssets = () => {
  const context = useContext(AssetContext);
  if (!context) {
    throw new Error("useAssets must be used within an AssetProvider");
  }
  return context;
};

/**
 * Hook to get the asset stats from provided filters and search term
 * @param {Object} filters - Filter object with vendor, location, telemetry and status arrays
 * @param {string} searchTerm - Search term to filter assets by
 * @returns {Object} Object containing filteredAssets and filteredFleetStats
 */
export const useFilteredAssets = (filters = {}, searchTerm = "") => {
  const { assets } = useAssets();

  const filteredAssets = useMemo(() => {
    if (!assets || !Array.isArray(assets)) return [];

    const {
      vendor = [],
      location = [],
      telemetrySource = [],
      status = [],
      assetNumbers = [],
      optimizationEnabled = null,
      foreman = [],
      customer = [],
      engineModel = [],
      compressorModel = [],
      horsepowerRange = [],
    } = filters;

    let filtered = assets.filter((asset) => {
      return (
        (arrayUtils.isEmpty(vendor) || vendor.includes(asset.vendor))
        && (arrayUtils.isEmpty(location) || location.includes(asset.location))
        && (arrayUtils.isEmpty(telemetrySource)
          || telemetrySource.includes(asset.telemetrySource))
        && arrayUtils.hasItems(status)
        && status.includes(asset.status)
        && (assetNumbers === null
          || assetNumbers === undefined
          || (Array.isArray(assetNumbers)
            && assetNumbers.length > 0
            && assetNumbers.includes(asset.assetNumber)))
        && matchesOptimizationFilter(
          asset.optimizationEnabled,
          optimizationEnabled
        )
        && (arrayUtils.isEmpty(foreman) || foreman.includes(asset.foreman))
        && (arrayUtils.isEmpty(customer) || customer.includes(asset.customer))
        && (arrayUtils.isEmpty(engineModel)
          || engineModel.includes(asset.engineModel))
        && (arrayUtils.isEmpty(compressorModel)
          || compressorModel.includes(asset.compressorModel))
        && matchesHorsepowerRangeFilter(asset.horsepower, horsepowerRange)
      );
    });

    if (searchTerm) {
      filtered = filterAssetsBySearch(
        filtered,
        searchTerm,
        formatTimestampForSearch
      );
    }

    return filtered;
  }, [assets, filters, searchTerm]);

  const filteredFleetStats = useMemo(() => {
    return calculateFleetStats(filteredAssets);
  }, [filteredAssets]);

  return {
    filteredAssets,
    filteredFleetStats,
  };
};

/**
 * Asset Provider component that provides global asset state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Asset provider component
 */
export function AssetProvider({ children }) {
  /** @type {[Compressor[], Function]} */
  const [assets, setAssets] = useState([]);

  /** @type {[boolean, Function]} */
  const [isLoading, setIsLoading] = useState(true);

  /** @type {[string|null, Function]} */
  const [error, setError] = useState(null);

  /** @type {[Date|null, Function]} */
  const [lastUpdatedTime, setLastUpdatedTime] = useState(null);

  // Get the shared data loader context
  const { triggerCount } = useDataLoaderContext();
  const { tenantRefreshKey, currentTenantId } = useSessionTenant();

  // This tracks whether assets need to be enriched with last fault info from downtime.
  const lastEnrichedDowntimeTimeRef = useRef(null);
  const lastHandledTriggerCountRef = useRef(null);

  /**
   * @param {unknown} apiResponse
   */
  const applyFleetResponse = (apiResponse) => {
    const transformedAssets = transformApiResponseToAssets(apiResponse);
    const nextAssets = Array.isArray(transformedAssets) ? transformedAssets : [];
    setAssets(nextAssets);
    setLastUpdatedTime(new Date());
    lastEnrichedDowntimeTimeRef.current = null;
  };

  /**
   * Fetch asset data from the fleet status API
   * @param {{ afterTenantSwitch?: boolean }} [options]
   * @returns {Promise<void>}
   */
  const fetchAssets = async (options = {}) => {
    const { afterTenantSwitch = false, isAbortRetry = false } = options;
    try {
      setIsLoading(true);
      setError(null);

      const apiResponse = await getFleetStatus();
      applyFleetResponse(apiResponse);
    } catch (err) {
      if (isRequestAborted(err)) {
        if (!isAbortRetry) {
          await fetchAssets({ ...options, isAbortRetry: true });
        }
        return;
      }
      if (
        afterTenantSwitch
        && err?.response?.status === 401
        && currentTenantId != null
      ) {
        setTenantSwitchInProgress(false);
        try {
          const apiResponse = await ensureFleetReady(currentTenantId);
          applyFleetResponse(apiResponse);
          return;
        } catch (recoverErr) {
          if (isRequestAborted(recoverErr)) return;
          err = recoverErr;
        }
      }
      console.error("Error loading fleet status from API:", err);
      setError("Failed to load fleet status data");
      setAssets([]);
    } finally {
      setIsLoading(false);
      if (afterTenantSwitch) {
        setTenantSwitchInProgress(false);
      }
    }
  };

  // Listen for triggerLoad from DataLoaderContext.
  // When AssetProvider mounts after a tenant switch (e.g. coming back from a
  // OneView-disabled tenant) triggerCount is already > 0 from the app-wide
  // DataLoader interval. The tenantRefreshKey effect below owns the post-switch
  // fetch, so skip the stale initial trigger to avoid a duplicate
  // getFleetStatus that races with the tenant-switch fetch.
  useEffect(() => {
    const isInitialTriggerEffect = lastHandledTriggerCountRef.current === null;
    lastHandledTriggerCountRef.current = triggerCount;
    if (isInitialTriggerEffect && tenantRefreshKey > 0) {
      return;
    }
    if (triggerCount > 0) {
      fetchAssets();
    }
  }, [triggerCount]);

  // Refetch fleet when session tenant changes
  useEffect(() => {
    if (tenantRefreshKey > 0) {
      // Drop stale assets synchronously so dependent effects (e.g. operating-points
      // tag-fetching) don't enqueue requests for the previous tenant's asset ids
      // while the new fleet load is in flight.
      setTenantSwitchInProgress(true);
      setAssets([]);
      lastEnrichedDowntimeTimeRef.current = null;
      fetchAssets({ afterTenantSwitch: true });
    }
  }, [tenantRefreshKey]);

  /**
   * Enrich assets with downtime data - get last fault info from the dowmtime data if available.
   * @param {Array} downtimeEvents - Array of downtime events grouped by asset.
   * @param {Date|null} downtimeLastUpdated - Timestamp when downtime events were last updated.
   */
  const enrichWithDowntime = useCallback(
    (downtimeEvents, downtimeLastUpdated) => {
      setAssets((currentAssets) => {
        // Only enrich if downtime was updated after we last enriched, or if we haven't enriched yet.
        // Otherwise circular refreshes will occur.
        const shouldEnrich =
          downtimeLastUpdated
          && (!lastEnrichedDowntimeTimeRef.current
            || downtimeLastUpdated.getTime()
              > lastEnrichedDowntimeTimeRef.current);

        if (
          currentAssets.length > 0
          && Array.isArray(downtimeEvents)
          && downtimeEvents.length > 0
          && shouldEnrich
        ) {
          const enrichedAssets = enrichAssetsWithDowntimeData(
            currentAssets,
            downtimeEvents
          );
          lastEnrichedDowntimeTimeRef.current = downtimeLastUpdated.getTime();
          return enrichedAssets;
        }
        return currentAssets;
      });
    },
    []
  );

  // Calculate fleet stats from current assets
  const fleetStats = calculateFleetStats(assets);

  /** @type {AssetContextType} */
  const value = {
    assets,
    fleetStats,
    isLoading,
    error,
    lastUpdatedTime,
    refetch: fetchAssets,
    enrichWithDowntime,
  };

  return (
    <AssetContext.Provider value={value}>{children}</AssetContext.Provider>
  );
}
